// ============================================================
// TRAVELER'S GUIDE — English Content
// ------------------------------------------------------------
// English mirror of /content/guide.js. Structure MUST match FR
// exactly (same slugs, same block types, same order) so a single
// renderer can switch between languages without any special case.
// ============================================================
import { MEDIA } from "@/content/media";

export const GUIDE_CONTENT_EN = {
  // ============================================================
  // ADDRESS
  // ============================================================
  adresse: {
    title: "The address",
    eyebrow: "Address & parking",
    hero: MEDIA.refuge.porteArrivee,
    blocks: [
      {
        type: "hero-text",
        eyebrow: "Curieux Refuge",
        value: "21 Rue des Pêches Cavales\nCarrosse — Saint-Gilles-les-Bains\nRéunion Island",
      },
      {
        type: "link",
        label: "Open in Google Maps",
        href: "https://www.google.com/maps/search/?api=1&query=21+Rue+des+P%C3%AAches+Cavales+Saint-Gilles-les-Bains",
      },
      {
        type: "callout",
        icon: "Car",
        label: "Parking",
        value:
          "A spot right in front of the house is usually free — we do our best to keep it for you. As it is a public spot, it may sometimes be taken: other spots are available nearby.",
      },
    ],
  },

  // ============================================================
  // ACCESS CODE
  // ============================================================
  "code-acces": {
    title: "The access code",
    eyebrow: "Self check-in",
    blocks: [
      {
        type: "hero-facts",
        facts: [
          { icon: "UserCheck", label: "Self check-in", value: "4 pm" },
          { icon: "Clock",     label: "Code sent",     value: "Around 2 pm" },
        ],
      },
      {
        type: "callout",
        icon: "KeyRound",
        label: "Personal code",
        value:
          "Your personal code will be sent to you around **2 pm** on the day of your arrival.",
      },
      {
        type: "photo-caption",
        src: MEDIA.refuge.boiteACles,
        alt: "Curieux Refuge key box",
        caption: "Key box",
        subCaption: "To the right of the front door",
      },
    ],
  },

  // ============================================================
  // TRANSPORT
  // ============================================================
  transport: {
    title: "Transport",
    eyebrow: "Getting around the island",
    intro:
      "The refuge is right next to bus stops serving Saint-Gilles and the whole west coast of the island.",
    blocks: [
      {
        type: "list",
        heading: "Bus stops near Curieux Refuge",
        items: [
          "École Carrosse",
          "Saint-Gilles Centre",
          "Roches Noires",
        ],
      },
      {
        type: "kv",
        heading: "Option 1 — M-Ticket / Car Jaune app",
        pairs: [
          ["Payment", "Directly on your phone"],
          ["Validation", "When boarding the bus"],
        ],
      },
      {
        type: "kv",
        heading: "Option 2 — Ticket on board",
        pairs: [
          ["Purchase", "Directly from the driver"],
          ["Tip", "Bring some coins"],
        ],
      },
      {
        type: "link",
        label: "Official Car Jaune Réunion website",
        href: "https://www.carjaune.re/",
      },
    ],
  },

  // ============================================================
  // WIFI
  // ============================================================
  wifi: {
    title: "Wifi",
    eyebrow: "Stay connected",
    intro: "The network covers the whole refuge, garden included.",
    blocks: [
      {
        type: "callout",
        icon: "Wifi",
        label: "Network name",
        value: "curieuxrefuge",
        mono: true,
      },
      {
        type: "callout",
        icon: "KeyRound",
        label: "Password",
        value: "CurieuxMotdepasse",
        mono: true,
      },
    ],
  },

  // ============================================================
  // EQUIPMENT
  // ============================================================
  equipements: {
    title: "Equipment",
    eyebrow: "Everything at hand",
    hero: MEDIA.refuge.chambre,
    intro:
      "The refuge is designed for two to three travelers. Here is what you'll find on site.",
    blocks: [
      {
        type: "list",
        heading: "The bedroom",
        items: [
          "Double bed 140 × 190 cm",
          "Cotton percale bed linen",
          "Soft + memory-foam pillows",
          "Duvet",
          "Air conditioning",
        ],
      },
      {
        type: "list",
        heading: "The shower room",
        items: [
          "Walk-in shower",
          "Cotton towels",
          "Shower gel",
          "Hairdryer",
          "Iron",
        ],
      },
      {
        type: "list",
        heading: "Outdoor & comfort",
        items: [
          "Mineral bath / mini pool",
          "Hammock",
          "Video projector",
        ],
      },
      {
        type: "section-title",
        title: "Laundry room",
        id: "buanderie",
      },
      {
        type: "callout",
        icon: "shirt",
        label: "On request",
        value: "Access to the laundry room: washing machine and clothes line. Access is not open by default — please let us know so we can grant you access.",
      },
      {
        type: "whatsapp-cta",
        label: "Request access on WhatsApp",
        message: "Hello, I would like access to the laundry room (washing machine + clothes line). Thank you!",
        sms: true,
        smsLabel: "By SMS",
      },
      {
        type: "list",
        heading: "For a 3rd traveler",
        items: ["Extra bed", "Travel cot on request"],
      },
    ],
  },

  // ============================================================
  // KITCHEN
  // ============================================================
  cuisine: {
    title: "The kitchen",
    eyebrow: "Cook as if you were home",
    hero: MEDIA.refuge.cuisine,
    intro:
      "A fully equipped kitchen for breakfasts, aperitifs and improvised meals — everything you need is already here.",
    blocks: [
      {
        type: "list",
        heading: "Appliances",
        items: [
          "Microwave (grill option)",
          "Fridge",
          "Mini freezer",
          "2-burner vitroceramic hob",
          "Coffee maker (ground coffee — pods available)",
          "Kettle",
        ],
      },
      {
        type: "list",
        heading: "Tableware & utensils",
        items: [
          "Full tableware for 3",
          "Wine, champagne and cocktail glasses",
          "Cooking utensils",
          "Sponge and dish soap provided",
        ],
      },
      {
        type: "list",
        heading: "Little extras",
        items: [
          "Salt, pepper, olive oil, vinegar",
          "Ground coffee and sugar",
        ],
      },
    ],
  },

  // ============================================================
  // POOL
  // ============================================================
  bassin: {
    title: "The mineral pool",
    eyebrow: "Eco & one of a kind",
    hero: MEDIA.refuge.bassin,
    intro:
      "A simple ritual, outdoors — warmed by the sun.",
    blocks: [
      {
        type: "stats-row",
        items: [
          { icon: "Sun",     value: "Solar",   label: "Water heater" },
          { icon: "Droplets",value: "400 L",   label: "Per day", subtext: "depending on the weather" },
          { icon: "Clock",   value: "35 min",  label: "Filling time" },
        ],
      },
      {
        type: "compare-two",
        heading: "Two experiences, one pool",
        left: {
          icon: "Droplets",
          title: "Clear water bath",
          anchor: "fonctionnement-eau-claire",
        },
        right: {
          icon: "Flower2",
          title: "Flower bath",
          anchor: "fonctionnement-bain-fleurs",
        },
      },
      {
        type: "section-title",
        id: "fonctionnement-eau-claire",
        eyebrow: "How it works",
        title: "The clear water bath",
      },
      {
        type: "callout",
        icon: "Droplets",
        label: "When you arrive",
        value:
          "The bath is not automatically filled — this way we can guarantee the ideal temperature at the moment you step in.",
      },
      {
        type: "callout",
        icon: "MessageCircle",
        label: "Would you like it ready?",
        value:
          "We'll be happy to prepare it. It will be filled halfway so you can add hot water at the moment of your choice.",
        whatsappCta: {
          label: "I'd like the pool pre-filled",
          message:
            "Hello, I would like the pool to be pre-filled for our arrival at Curieux Refuge, thank you!",
        },
      },
      {
        type: "callout",
        icon: "Recycle",
        label: "Saving water",
        value:
          "There is no need to empty the pool after each use — this preserves water and fits our eco-conscious approach.",
      },
      {
        type: "callout",
        icon: "Sparkles",
        label: "Keeping the water clear",
        value:
          "A shower before you step in keeps the water clean. A skimmer is at your disposal to remove leaves and petals.",
      },
      {
        type: "callout",
        icon: "Timer",
        label: "How long it lasts",
        value:
          "The bath can be kept up to **48 hours**. You can empty part of it and add hot water for a new immersion.",
      },
      {
        type: "video-coming",
        label: "Filling video",
      },
      {
        type: "callout",
        icon: "Clock",
        label: "Longer stays",
        value:
          "After **48 hours**, fully empty the pool then refill it following the video.",
      },
      {
        type: "section-title",
        id: "fonctionnement-bain-fleurs",
        eyebrow: "How it works",
        title: "The flower bath",
      },
      {
        type: "callout",
        icon: "Flower2",
        label: "An ephemeral experience",
        value:
          "The flower bath is booked as a special moment — sprinkled with petals, it cannot be kept beyond **12 hours**.",
      },
      {
        type: "callout",
        icon: "Sparkles",
        label: "Back to clear water",
        value:
          "To go back to a clear water bath, remove the petals using the skimmer and squeegee, then drop them in the brown composting bin. The pool can then be filled again with clear water.",
      },
      {
        type: "callout",
        icon: "Recycle",
        label: "The day of your departure",
        value:
          "If you don't plan a new bath, let us handle it — we take care of cleaning the flower bath.",
      },
      {
        type: "link",
        label: "🌸 All about the flower bath",
        href: "/experiences",
      },
      {
        type: "section-title",
        eyebrow: "Departure",
        title: "Before you leave",
      },
      {
        type: "callout",
        icon: "Recycle",
        label: "Nothing required",
        value:
          "There's no need to empty the pool before you leave. The water is filtered and reused to water the garden — an eco-friendly cycle.",
      },
      {
        type: "warning",
        heading: "Good to know",
        items: [
          "Avoid fully emptying the clear water bath if it is less than 48 hours old",
          "You can always empty part of it and add hot water for a new immersion",
          "A shower before stepping in keeps the water clear",
          "Do not add soap",
          "Do not add oil",
          "Do not add bubble bath products",
        ],
      },
      {
        type: "note",
        text:
          "At Curieux Refuge, every bath is prepared with care to offer a moment of relaxation while respecting natural resources.",
      },
    ],
  },

  // ============================================================
  // LIGHTING & SOUND
  // ============================================================
  eclairage: {
    title: "Lighting & sound",
    eyebrow: "Light, music, cinema",
    intro: "Find every switch at a glance.",
    blocks: [
      {
        type: "visual-pair",
        eyebrow: "Kitchen lamp",
        photos: [
          MEDIA.refuge.lampadaireCuisine,
          MEDIA.refuge.interrupteurLampadaire,
        ],
      },
      {
        type: "visual-pair",
        eyebrow: "Fairy lights",
        photos: [
          MEDIA.refuge.guirlandeLumineuse,
          MEDIA.refuge.telecommandeGuirlande,
        ],
        note: "Remote next to the microwave.",
      },
      {
        type: "visual-pair",
        eyebrow: "Bedroom",
        photos: [
          MEDIA.refuge.luminaireChambre,
          MEDIA.refuge.interrupteurChambre,
        ],
        note: "Switch near the shower room door.",
      },
      {
        type: "visual-pair",
        eyebrow: "Music",
        photos: [MEDIA.refuge.telecommandeMusique],
        note: "Yamaha remote available · Bluetooth speaker in the living room, direct pairing from your phone.",
      },
      {
        type: "visual-pair",
        eyebrow: "Projector",
        photos: [
          MEDIA.refuge.retroprojecteur,
          MEDIA.refuge.retroClimBougie,
        ],
        note: "Remote available. Bluetooth connection and access to your favourite streaming platforms.\nOn the same shelf: A/C remote and remote to switch off the candles.",
      },
    ],
  },

  // ============================================================
  // AIR CONDITIONING
  // ============================================================
  climatisation: {
    title: "Air conditioning",
    eyebrow: "Find just the right coolness",
    intro:
      "The refuge stays naturally pleasant, but the A/C is here for extra comfort when you need it.",
    blocks: [
      {
        type: "callout",
        icon: "Wind",
        label: "Remote",
        value: "On the bedside table.",
      },
      {
        type: "list",
        heading: "Good habits",
        items: [
          "Close doors and windows while the A/C is running.",
          "Switch off the A/C when leaving the refuge.",
        ],
      },
    ],
  },

  // ============================================================
  // HOUSE RULES
  // ============================================================
  regles: {
    title: "House rules",
    eyebrow: "Living together, respecting the place",
    intro:
      "Four simple rules so everyone can fully enjoy the refuge.",
    blocks: [
      {
        type: "kv",
        heading: "Neighbours",
        pairs: [
          [
            "01",
            "Enjoy music and good times — as long as everything stays calm and respectful.",
          ],
        ],
      },
      {
        type: "kv",
        heading: "Careful with fire",
        pairs: [
          [
            "02",
            "The interior is non-smoking. You may smoke outside, taking care to preserve the place. Candles with open flames are not allowed.",
          ],
        ],
      },
      {
        type: "kv",
        heading: "Guests",
        pairs: [
          [
            "03",
            "You are welcome to have visitors. The furniture is designed for 2 to 3 people, according to your booking.",
          ],
        ],
      },
      {
        type: "kv",
        heading: "Cleaning & dishes",
        pairs: [
          [
            "04",
            "Dishes should be left clean on the drying rack — we'll put them back in the cupboards.",
          ],
        ],
      },
    ],
  },

  // ============================================================
  // EMERGENCIES
  // ============================================================
  urgences: {
    title: "In case of emergency",
    eyebrow: "Numbers and key gestures",
    intro:
      "We hope you won't need any of this — but everything useful is right here.",
    blocks: [
      {
        type: "callout",
        icon: "Flame",
        label: "Fire extinguisher",
        value: "Under the kitchen sink.",
      },
      {
        type: "kv",
        heading: "Emergency numbers",
        pairs: [
          ["SAMU (medical)", "15"],
          ["Police", "17"],
          ["Fire brigade", "18"],
        ],
      },
      {
        type: "list",
        heading: "Health — nearby",
        items: [
          "CHOR Saint-Paul Hospital",
          "Pharmacie de Roquefeuil",
          "Pharmacie de l'Océan",
          "Pharmacie de Saint-Gilles",
          "SOS Médecin Saint-Paul (on-call doctor)",
        ],
      },
    ],
  },

  // ============================================================
  // CONTACT
  // ============================================================
  contact: {
    title: "Contact & socials",
    eyebrow: "Writing to Eva & Gabrielle",
    intro:
      "We remain happily available throughout your stay.",
    blocks: [
      {
        type: "tel",
        label: "Call Eva & Gabrielle",
        value: "+262 692 79 31 55",
        href: "tel:+262692793155",
        secondary: {
          label: "WhatsApp",
          value: "Write to us",
          href: "https://wa.me/262692793155",
        },
      },
      {
        type: "mail",
        label: "Email",
        value: "curieuxrefuge.carosse@gmail.com",
        href: "mailto:curieuxrefuge.carosse@gmail.com",
      },
      {
        type: "insta",
        label: "Instagram",
        value: "@curieuxrefugesaintgilles",
        href: "https://instagram.com/curieuxrefugesaintgilles",
      },
    ],
  },

  // ============================================================
  // DEPARTURE
  // ============================================================
  depart: {
    title: "Departure",
    eyebrow: "Closing the refuge door",
    hero: MEDIA.refuge.jardinEntree,
    intro:
      "A simple, stress-free departure — we take care of the rest.",
    blocks: [
      {
        type: "callout",
        icon: "Clock",
        label: "Check-out time",
        value: "12 pm",
      },
      {
        type: "list",
        heading: "Already taken care of",
        items: [
          "You can leave the bed as is — we take care of the linen.",
          "Just leave your clean dishes on the drying rack, we will put them away.",
        ],
      },
      {
        type: "steps",
        heading: "Before you leave",
        items: [
          "Make sure everything is closed and switched off.",
          "Put the keys back in the key box.",
          "Scramble the box code.",
          "Send us a quick message to let us know.",
        ],
      },
    ],
  },
};

export default GUIDE_CONTENT_EN;
